import { useCallback, useMemo, useRef, useState } from 'react';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { useCountryContext } from '@archipro-website/localisation';
import { useLoaderData } from 'react-router';
import { ArrowDown, MessageCircle } from 'lucide-react';

import { cn } from '@rocco/utils/cn';
import {
    ARCHIPRO_LOOK_BOOK_ID,
    ARCHIPRO_SPEC_SHEET_ID,
    archiproLookBookFrom,
} from '@modules/product/tear-sheet/archipro-spec-sheet';
import { useGatedDownloads } from '@modules/product/hook/use-gated-downloads';
import { useProductFilesModalDeepLink } from '@modules/product/hook/use-product-files-modal-deep-link';
import { DownloadSurface } from 'generated/graphql';
import {
    DownloadSaveButton,
    type GeneratedDocument,
} from '~/ui-rocco/pages/detail-shared/DownloadSaveButton';
import type { loader } from '~/routes/_app.product.$urlSegment';
import {
    CONTENT_CAP_PADDED,
    detailCta,
    LoadMoreButton,
} from '~/ui-rocco/pages/detail-shared';
import { button, SURFACE_HOVER } from '~/ui-rocco/button-system';
import { useEnquireCta } from '~/modules/enquiry/hooks/use-page-ctas';
import {
    downloadTrackingAttributes,
    fileRecordID,
    ProductDownloadPicker,
} from './ProductDownloadPicker';
import { groupDownloadsByCategory } from './group-downloads-by-category';

/**
 * Every kind of file a supplier can attach, in the order Business Manager
 * lists them.
 *
 * The section shows all of them, not just the ones that exist. A specifier
 * looking for a BPIR needs to know the row is there and can be asked for —
 * an absent row reads as "this product has no BPIR", which is a different
 * claim from "the supplier has not uploaded one".
 *
 * Mirrored from the CMS taxonomy rather than read from it: the categories are
 * a `FileCategory` DataObject the product query does not currently return, and
 * only the categories with a file attached come back with the downloads. Worth
 * replacing with a real query when one exists — until then this list has to be
 * kept in step by hand.
 */
const CATEGORIES = [
    'Spec Sheets',
    'CAD and BIM',
    'Installation Details',
    'Certifications',
    'Catalogues',
    'BPIR',
    'Masterspec Sheets',
    'Product Care Guide',
    'Compliance',
    'Scope Documents',
    'Performance Documents',
] as const;

/**
 * Categories that only mean something in New Zealand. BPIR is a New Zealand
 * building regulation, so elsewhere an empty BPIR row asks for a document the
 * reader has no use for. A file the supplier did upload under it still shows.
 */
const NZ_ONLY_CATEGORIES: ReadonlySet<string> = new Set<
    (typeof CATEGORIES)[number]
>(['BPIR']);

/** How many rows show before the reader asks for the rest. */
const SHOWN = 5;

/**
 * The sheet ArchiPro generates for every product, first and under its own name.
 *
 * The loader injects it into the supplier's Spec Sheets group so a supplier
 * cannot delete it, which is right for the data and wrong for the list: it made
 * the one file every product is guaranteed to have look like something the
 * supplier had uploaded. Named for who made it, it is the row a reader can
 * always count on.
 */
const TEAR_SHEET_LABEL = 'ArchiPro Tear Sheet';

/** The product's look book, the tear sheet's longer companion, under it. */
const LOOK_BOOK_LABEL = 'ArchiPro Look Book';

/** The small grey type the band's two corners are set in. */
const META =
    'm-0 font-sans text-[0.875rem] leading-[1.3] tracking-[0.01em] text-[#767676]';

/**
 * The product's files, as a band of their own under the details.
 *
 * They used to be the first row of the spec accordion, on the reasoning that a
 * specifier comes for the files and should meet them among the facts. In
 * practice a row that opens into a list of five is a row that hides four of
 * them, and the accordion made the most-wanted thing on the page the same size
 * as the pricing line. Here each file is a line of its own at heading size,
 * numbered, with its action on the right.
 *
 * The action says what the row can give you. A file that exists says Download,
 * whether or not a gate stands between the click and the file — the gate is a
 * step on the way, not a different outcome. Only a category the supplier has
 * filled nothing into says Request.
 */
export const ProductDownloadsSection = () => {
    const { product } = useLoaderData<typeof loader>();
    const { mobile } = useAppDisplayModeContext();
    const { code: market } = useCountryContext();
    const [expanded, setExpanded] = useState(false);
    // The category row whose files the picker is showing, if any.
    const [picking, setPicking] = useState<string | null>(null);
    const thumbnail =
        product.LibraryImages?.[0]?.LibraryImageFile?.DetailPageSquare
            ?.Desktop ?? undefined;
    // A row the supplier has filled nothing into has no file to gate — asking
    // for it is an enquiry, so it opens the product's enquiry modal with the
    // category as the subject rather than the download gate. With the page's
    // Enquire button hidden there is no way to ask, so those rows drop out.
    const enquire = useEnquireCta({ source: 'ProductDetailsCTA' });
    const canRequest = enquire.visible;
    const {
        isLoggedIn,
        isGated: downloadsGated,
        handleGatedDownload,
        registerDownloadLead,
    } = useGatedDownloads({
        surface: DownloadSurface.ProductFiles,
        libraryItemID: product.ID,
        professionalID: product.Professional.ID,
        itemImage: thumbnail,
    });

    /**
     * One row per category, with the supplier's files on it when there are any.
     * A row holding more than one opens a picker rather than a file.
     *
     * Ordered by what the reader can act on: the files that exist come first,
     * so the five rows on show are the useful ones and the rest are behind
     * Load more. Within each half the CMS order stands.
     */
    const rows = useMemo(() => {
        const held = (product.LibraryDownloads ?? []).filter(
            (download) => download.InternalLink || download.ExternalLink,
        );
        const tearSheet = held.find(
            (download) => download.ID === ARCHIPRO_SPEC_SHEET_ID,
        );
        const all = groupDownloadsByCategory(
            held.filter((download) => download.ID !== ARCHIPRO_SPEC_SHEET_ID),
            CATEGORIES,
        );
        const lookBook = tearSheet ? archiproLookBookFrom(tearSheet) : null;

        return [
            // Always first, ahead of even the supplier's own files: it is the
            // one row that is never empty.
            ...(tearSheet
                ? [{ name: TEAR_SHEET_LABEL, downloads: [tearSheet] }]
                : []),
            // Null when the tear sheet's address is not the generated one, and
            // then there is no look book to offer beside it.
            ...(lookBook
                ? [{ name: LOOK_BOOK_LABEL, downloads: [lookBook] }]
                : []),
            ...all.filter((row) => row.downloads.length > 0),
            ...(canRequest
                ? all.filter(
                      (row) =>
                          row.downloads.length === 0 &&
                          (market === 'NZ' ||
                              !NZ_ONLY_CATEGORIES.has(row.name)),
                  )
                : []),
        ];
    }, [product.LibraryDownloads, canRequest, market]);

    // Files, not rows: a category holding two is two downloads.
    const available = rows.reduce(
        (count, row) => count + row.downloads.length,
        0,
    );
    const requestable = rows.filter((row) => row.downloads.length === 0).length;
    // What the reader can act on now, and what costs them an enquiry — which
    // is the distinction that matters here, where "1 of 12 available" only
    // said how few there were. Either half drops out when it is empty rather
    // than printing a zero.
    const summary = [
        available > 0 &&
            `${available} Direct download${available === 1 ? '' : 's'}`,
        requestable > 0 && `${requestable} Available on request`,
    ]
        .filter(Boolean)
        .join(' • ');
    const visible = expanded ? rows : rows.slice(0, SHOWN);
    const owner = {
        productID: product.ID,
        professionalID: product.Professional?.ID,
        professionalTitle: product.Professional?.Title,
    };
    const pickingRow = rows.find(
        (row) => row.name === picking && row.downloads.length > 1,
    );
    const sectionRef = useRef<HTMLElement>(null);

    const scrollToProductFiles = useCallback(() => {
        sectionRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    }, []);

    useProductFilesModalDeepLink({
        enabled: true,
        open: false,
        onOpen: scrollToProductFiles,
    });

    // No files, and no one to ask for them.
    if (rows.length === 0) return null;

    return (
        // The band closes on its own air rather than on the Show all button:
        // with the white section that follows starting immediately under it,
        // the control was sitting on the seam between the two grounds.
        <section
            ref={sectionRef}
            id="product-files"
            className="scroll-mt-24 bg-ground max-md:px-6 max-md:pb-16 md:px-[3rem] md:pb-[7.5rem]"
        >
            <div className={CONTENT_CAP_PADDED}>
                <div
                    className={cn(
                        // On the phone the two corners stacked into what read
                        // as a wrapped spec row; the band's name is set as its
                        // heading with the counts on their own line under it.
                        'flex max-md:flex-col max-md:gap-2',
                        'md:items-baseline md:justify-between md:gap-4',
                        // The count lines up with the buttons below it, not
                        // with the band's edge: the rows run wider than this
                        // header and sit their control 48px inside it, so
                        // without the inset the count hangs past the whole
                        // column it is meant to be labelling.
                        'md:pr-[3rem]',
                    )}
                >
                    {/* On the phone the band has no heading: its one line
                        names it and counts what can be taken now. */}
                    <p className={cn(META, 'max-md:hidden')}>Downloads</p>
                    <p className={META}>
                        <span className="md:hidden">
                            Downloads ({available})
                        </span>
                        <span className="max-md:hidden">{summary}</span>
                    </p>
                </div>

                {/* The list, not the row, is what puts the card away: hiding
                    it on each row's own leave faded it out and back in at every
                    boundary on the way down. */}
                <ul className="m-0 mt-[1.75rem] list-none p-0">
                    {visible.map((row, index) => {
                        const download = row.downloads[0];
                        // More than one file behind the row: the click opens
                        // the picker, and saving happens there, per file.
                        const hasChoice = row.downloads.length > 1;
                        // One rule for every file, the supplier's own and the
                        // tear sheet ArchiPro renders: nothing opens until the
                        // reader has an account, and taking it is the lead.
                        const isGated = Boolean(download) && downloadsGated;
                        // Only an empty row asks. A gated file is still a
                        // download — the gate is a step on the way to it, not
                        // a different outcome — and calling it Request told the
                        // reader the file might not exist when it does.
                        const mustAsk = !download;
                        const link = download
                            ? download.ExternalLink ||
                              download.InternalLink ||
                              ''
                            : '';

                        // The whole row is the control, and the pill is a
                        // label on it. Two hit targets in one line — a small
                        // button on the right and a large title on the left
                        // that did nothing — is a row that looks clickable
                        // everywhere and is clickable in one place.
                        const inner = (
                            <>
                                <span className="flex min-w-0 items-baseline max-md:gap-4 md:gap-[5.75rem]">
                                    <span
                                        aria-hidden="true"
                                        className="shrink-0 font-sans text-[0.8125rem] text-[#a5a5a8] tabular-nums"
                                    >
                                        {String(index + 1).padStart(2, '0')}
                                    </span>
                                    <span
                                        className={cn(
                                            // Clamped, one line on desktop and
                                            // two on the phone, breaking
                                            // anywhere: a file named with no
                                            // spaces (spec_24D074_24D075_…)
                                            // otherwise sets the row's minimum
                                            // width and pushes the page wider
                                            // than the window.
                                            'min-w-0 text-left font-sans font-medium [overflow-wrap:anywhere] max-md:line-clamp-2 max-md:text-[1.125rem] max-md:leading-[1.25] md:line-clamp-1 md:text-[2rem] md:leading-[1.2] md:tracking-[-0.02em]',
                                            // A row with nothing behind it is
                                            // quieter than one that has a file
                                            // waiting, so the list reads at a
                                            // glance.
                                            download
                                                ? 'text-[#323232]'
                                                : 'text-[#9a9a9d]',
                                        )}
                                        title={row.name}
                                    >
                                        {row.name}
                                    </span>
                                </span>

                                <span
                                    className={cn(
                                        // A row holding a file gets the pill;
                                        // a row with nothing behind it gets
                                        // the label alone. The pill is the
                                        // promise of a file, so spending it on
                                        // a request read as though every row
                                        // had one waiting.
                                        // On the phone the pill is its icon
                                        // alone, ranged right — the labelled
                                        // pill crowded the title into a wrap
                                        // on a 414 measure.
                                        // The request disc goes white on the
                                        // phone — flat, a step quieter than
                                        // the lifted download disc — because
                                        // a ghost icon alone on the grey band
                                        // did not read as a control at all.
                                        mustAsk
                                            ? button(
                                                  'ghost',
                                                  'l',
                                                  // No right padding on desktop:
                                                  // the ghost has no visible box,
                                                  // so its padding would leave the
                                                  // words short of the edge the
                                                  // save button on the rows above
                                                  // sits on.
                                                  'shrink-0 md:pr-0 max-md:size-[2.75rem] max-md:justify-center max-md:rounded-[var(--radius-btn-m)] max-md:bg-white max-md:p-0',
                                              )
                                            : detailCta(
                                                  'white-lifted',
                                                  'shrink-0 max-md:size-[2.75rem] max-md:justify-center max-md:rounded-[var(--radius-btn-m)] max-md:p-0',
                                              ),
                                        // Not a control of its own — the row
                                        // around it takes the click, and a
                                        // nested button here would be both
                                        // invalid and a second tab stop for
                                        // the same action.
                                        'pointer-events-none',
                                    )}
                                >
                                    <span className="max-md:hidden">
                                        {mustAsk ? 'Request' : 'Download'}
                                    </span>
                                    {mustAsk ? (
                                        <MessageCircle
                                            aria-hidden="true"
                                            className="size-[1.125rem] shrink-0"
                                        />
                                    ) : (
                                        <ArrowDown
                                            aria-hidden="true"
                                            className="size-[1.125rem] shrink-0"
                                        />
                                    )}
                                </span>
                            </>
                        );

                        const rowClass = cn(
                            'flex min-w-0 flex-1 cursor-pointer items-center justify-between gap-6 bg-transparent text-left no-underline hover:no-underline',
                            'max-md:gap-4 max-md:py-5 md:py-[1.5rem]',
                        );

                        // The hairline and the hover tint sit on the row rather
                        // than on the control, so the save button beside it is
                        // inside the same row without being inside the control.
                        // A second interactive element within the button or the
                        // anchor would be invalid and a second tab stop for the
                        // wrong action.
                        const rowShellClass = cn(
                            'flex items-center gap-3 border-t border-[#e2e2e4] transition-colors',
                            'hover:bg-[#ededf0]',
                            // The tint runs to the band's edge rather than
                            // stopping at the text, so the hover reads as the
                            // row lighting up rather than as a box appearing
                            // inside it. Desktop only — the phone has no
                            // hover, and the bleed left the hairlines hanging
                            // outside the page's gutter.
                            'md:-mx-6 md:px-6',
                        );

                        const isTearSheet =
                            download?.ID === ARCHIPRO_SPEC_SHEET_ID;
                        // Both are rendered on request and stored nowhere, so
                        // both are pinned by the product rather than by a file.
                        const generated: GeneratedDocument | null = isTearSheet
                            ? 'TearSheet'
                            : download?.ID === ARCHIPRO_LOOK_BOOK_ID
                              ? 'Lookbook'
                              : null;
                        // ArchiPro's own documents are rows of this band but
                        // not the supplier's files, and the surface is what
                        // separates them. The lookbook had been reported as an
                        // ordinary supplier file — `ProductLookbook` existed in
                        // the schema with no caller at all.
                        const takenFrom =
                            generated === 'Lookbook'
                                ? DownloadSurface.ProductLookbook
                                : isTearSheet
                                  ? DownloadSurface.ProductTearSheet
                                  : DownloadSurface.ProductFiles;
                        const fileTitle = download?.Title ?? row.name;
                        const fileID = download
                            ? fileRecordID(download.ID)
                            : undefined;
                        return (
                            <li
                                key={`${row.name}-${index}`}
                                className={rowShellClass}
                            >
                                {mustAsk || isGated || hasChoice ? (
                                    <button
                                        type="button"
                                        aria-haspopup={
                                            hasChoice ? 'dialog' : undefined
                                        }
                                        onClick={() =>
                                            hasChoice
                                                ? setPicking(row.name)
                                                : mustAsk
                                                  ? enquire.open({
                                                        title: `Request ${row.name}`,
                                                        ctaLabel:
                                                            'Send Request',
                                                        messageSubject: `${row.name} for ${product.Title}`,
                                                    })
                                                  : handleGatedDownload(
                                                        link,
                                                        takenFrom,
                                                        {
                                                            title: fileTitle,
                                                            id: fileID,
                                                            professionalTitle:
                                                                product
                                                                    .Professional
                                                                    ?.Title,
                                                        },
                                                    )
                                        }
                                        className={rowClass}
                                    >
                                        {inner}
                                    </button>
                                ) : (
                                    <a
                                        href={link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        download={
                                            download?.ExternalLink
                                                ? undefined
                                                : ''
                                        }
                                        // Tracked the way every other download
                                        // anchor is: the delegated mousedown
                                        // listener in useLogLinkClicks reads
                                        // these and logs DownloadsClick. The
                                        // revamp rendered a bare anchor, so a
                                        // product download — a conversion —
                                        // reported nothing at all.
                                        // The surface is the one the gated
                                        // path reports as Source, so an
                                        // entitled reader's tear sheet or
                                        // lookbook is still separable from a
                                        // supplier's own file.
                                        {...downloadTrackingAttributes({
                                            surface: takenFrom,
                                            fileID,
                                            fileTitle,
                                            owner,
                                        })}
                                        className={rowClass}
                                    >
                                        {inner}
                                    </a>
                                )}
                                {/* Only a row with a file has something to
                                    save; an empty one is a request, and there
                                    is nothing yet to put on a board. */}
                                {download && !hasChoice ? (
                                    <span className="flex shrink-0 items-center">
                                        <DownloadSaveButton
                                            {...(generated
                                                ? ({
                                                      kind: 'generated',
                                                      document: generated,
                                                  } as const)
                                                : ({
                                                      kind: 'file',
                                                      fileId: download.ID,
                                                  } as const))}
                                            title={row.name}
                                            itemId={product.ID}
                                            tone="white"
                                            // Saving a Specs file reaches it
                                            // as surely as downloading does,
                                            // so it owes the same lead. Only
                                            // once signed in: signing in is
                                            // what the save itself asks for
                                            // first.
                                            onSaveIntent={
                                                isGated && isLoggedIn
                                                    ? registerDownloadLead
                                                    : undefined
                                            }
                                        />
                                    </span>
                                ) : null}
                                {/* Where a single file's save sits, a row of
                                    several says how many there are; saving is
                                    per file, in the picker it opens. */}
                                {hasChoice ? (
                                    <span className="flex shrink-0 items-center">
                                        <button
                                            type="button"
                                            aria-haspopup="dialog"
                                            aria-label={`${row.downloads.length} ${row.name} files to choose from`}
                                            onClick={() => setPicking(row.name)}
                                            className={cn(
                                                'detail-cta-pill inline-flex h-[3.25rem] min-w-[3.25rem] shrink-0 items-center justify-center rounded-[var(--radius-btn-l)] border border-transparent bg-white px-[0.875rem] font-sans text-base leading-none font-medium text-[#181818] tabular-nums shadow-[0px_4px_14px_10px_rgba(0,0,0,0.05)] transition-colors',
                                                SURFACE_HOVER,
                                            )}
                                        >
                                            {row.downloads.length}
                                        </button>
                                    </span>
                                ) : null}
                            </li>
                        );
                    })}
                </ul>

                {rows.length > SHOWN ? (
                    <div className="border-t border-[#e2e2e4]">
                        <LoadMoreButton
                            onClick={() => setExpanded(!expanded)}
                            label={
                                expanded
                                    ? 'Show less'
                                    : // The phone's band has no heading, so
                                      // the button names what it opens.
                                      mobile
                                      ? 'Show all Downloads'
                                      : `Show all ${rows.length}`
                            }
                            expanded={expanded}
                            className="pt-[2.5rem]"
                        />
                    </div>
                ) : null}
            </div>
            <ProductDownloadPicker
                category={pickingRow?.name ?? ''}
                downloads={pickingRow?.downloads ?? null}
                owner={owner}
                isGated={downloadsGated}
                isLoggedIn={isLoggedIn}
                onGatedDownload={(download) =>
                    handleGatedDownload(
                        download.ExternalLink || download.InternalLink || '',
                        DownloadSurface.ProductFiles,
                        {
                            title: download.Title,
                            id: fileRecordID(download.ID),
                            professionalTitle: product.Professional?.Title,
                        },
                    )
                }
                onSaveIntent={
                    downloadsGated && isLoggedIn
                        ? registerDownloadLead
                        : undefined
                }
                onClose={() => setPicking(null)}
            />
        </section>
    );
};
